// screens/admin/AdminProductsListScreen.jsx

import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";

import {
  deleteAdminProduct,
  listAdminProducts,
  resetAdminProductDelete,
  resetAdminProductSetStatus,
  restoreAdminProduct,
  setAdminProductStatus,
} from "../../actions/adminProductActions";

import { SHOPS, isValidShopKey } from "../../constants/shopMeta";
import { getSafeUserMessage } from "../../utils/errorMessages";

import "./AdminProductsListScreen.css";

/* -------------------------------------------------------------------------- */
/*                                  Constants                                 */
/* -------------------------------------------------------------------------- */

const ALL_SHOPS_KEY = "all";

const SHOP_OPTIONS = [
  { value: ALL_SHOPS_KEY, label: "All shops" },
  ...SHOPS.map((shop) => ({
    value: shop.shopKey,
    label: shop.name,
  })),
];

const SHOP_LABEL_BY_KEY = SHOP_OPTIONS.reduce((acc, option) => {
  acc[option.value] = option.label;
  return acc;
}, {});

const STATUS_OPTIONS = [
  { value: "all", label: "All" },
  { value: "draft", label: "Draft" },
  { value: "published", label: "Published" },
  { value: "deleted", label: "Deleted" },
];

const SORT_OPTIONS = [
  { value: "newest", label: "Newest" },
  { value: "oldest", label: "Oldest" },
  { value: "price_asc", label: "Price ↑" },
  { value: "price_desc", label: "Price ↓" },
  { value: "name_asc", label: "Name A→Z" },
  { value: "name_desc", label: "Name Z→A" },
];

const VALID_STATUS = new Set(STATUS_OPTIONS.map((item) => item.value));
const VALID_SORT = new Set(SORT_OPTIONS.map((item) => item.value));

const DEFAULT_SHOP_KEY = ALL_SHOPS_KEY;
const DEFAULT_STATUS = "all";
const DEFAULT_SORT = "newest";
const PAGE_LIMIT = 24;
const SEARCH_DEBOUNCE_MS = 350;

/* -------------------------------------------------------------------------- */
/*                                   Helpers                                  */
/* -------------------------------------------------------------------------- */

function safeText(value = "") {
  return String(value ?? "").trim();
}

function normalizeShopKey(value = "") {
  const next = safeText(value);

  if (!next || next === ALL_SHOPS_KEY) return ALL_SHOPS_KEY;
  return isValidShopKey(next) ? next : DEFAULT_SHOP_KEY;
}

function normalizeStatus(value = "") {
  const next = safeText(value).toLowerCase();
  return VALID_STATUS.has(next) ? next : DEFAULT_STATUS;
}

function normalizeSort(value = "") {
  const next = safeText(value);
  return VALID_SORT.has(next) ? next : DEFAULT_SORT;
}

function normalizePage(value, fallback = 1) {
  const page = Math.floor(Number(value));
  return Number.isFinite(page) && page > 0 ? page : fallback;
}

function cleanParams(params = {}) {
  return Object.fromEntries(
    Object.entries(params).filter(([, value]) => {
      if (value === undefined || value === null) return false;
      if (typeof value === "string" && value.trim() === "") return false;
      return true;
    }),
  );
}

function getApiShopKey(shopKey) {
  return shopKey === ALL_SHOPS_KEY ? "" : shopKey;
}

function getShopLabel(shopKey) {
  return SHOP_LABEL_BY_KEY[shopKey] || shopKey || "All shops";
}

function getProductId(product = {}) {
  return safeText(product?._id || product?.id);
}

function getProductStatus(product = {}) {
  const explicit = safeText(product?.status).toLowerCase();

  if (explicit) return explicit;
  if (product?.isDeleted) return "deleted";
  if (product?.isPublished) return "published";

  return "draft";
}

function getStatusBadgeClass(status = "") {
  switch (safeText(status).toLowerCase()) {
    case "published":
      return "admin-badge admin-badge--success";
    case "draft":
      return "admin-badge admin-badge--warning";
    case "deleted":
      return "admin-badge admin-badge--muted";
    default:
      return "admin-badge";
  }
}

function formatMoney(value) {
  const amount = Number(value);
  return Number.isFinite(amount) ? amount.toFixed(2) : "0.00";
}

function formatDate(value) {
  if (!value) return "—";

  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "—" : date.toLocaleDateString();
}

function normalizeErrorMessage(errorLike) {
  if (!errorLike) return "";
  if (typeof errorLike === "string") {
    return getSafeUserMessage(
      errorLike,
      "We could not update products right now. Please try again later.",
    );
  }

  return getSafeUserMessage(
    errorLike?.message,
    "We could not update products right now. Please try again later.",
  );
}

function isConflictError(errorMeta) {
  return Number(errorMeta?.status) === 409;
}

function getConflictMessage(errorMeta) {
  const code = safeText(errorMeta?.code);

  switch (code) {
    case "VERSION_CONFLICT":
      return "This product was modified by another request. Refresh the list and try again.";
    case "PRODUCT_SKU_EXISTS":
      return "A product or variant with this SKU already exists.";
    case "PRODUCT_SLUG_EXISTS":
      return "A product with this slug already exists in this shop.";
    case "PRODUCT_EXTERNAL_ID_EXISTS":
      return "A product with this external ID already exists in this shop.";
    default:
      return normalizeErrorMessage(errorMeta) || "Conflict detected.";
  }
}

function getAvailableLabel(product = {}) {
  if (typeof product?.available === "number") return product.available;
  if (typeof product?.countAvailable === "number")
    return product.countAvailable;
  if (typeof product?.countInStock === "number") return product.countInStock;
  if (product?.hasStock) return "In stock";

  return "Out";
}

function getPaginationState(listState = {}) {
  const pagination = listState.pagination || {};

  const page = normalizePage(pagination.page || listState.page || 1);
  const totalPages = Math.max(
    1,
    Number(pagination.totalPages || listState.pages || 1),
  );
  const total = Math.max(0, Number(pagination.total ?? listState.count ?? 0));

  return {
    page,
    totalPages,
    total,
    hasNextPage:
      typeof pagination.hasNextPage === "boolean"
        ? pagination.hasNextPage
        : page < totalPages,
    hasPrevPage:
      typeof pagination.hasPrevPage === "boolean"
        ? pagination.hasPrevPage
        : page > 1,
  };
}

/* -------------------------------------------------------------------------- */
/*                                   Screen                                   */
/* -------------------------------------------------------------------------- */

const AdminProductsListScreen = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [confirmingId, setConfirmingId] = useState("");
  const [searchInput, setSearchInput] = useState(
    safeText(searchParams.get("search") || searchParams.get("keyword")),
  );
  const [mutatingProductId, setMutatingProductId] = useState("");

  const refreshAfterMutationRef = useRef(false);
  const lastToastRef = useRef("");

  const adminProductList = useSelector((state) => state.adminProductList || {});
  const adminProductDelete = useSelector(
    (state) => state.adminProductDelete || {},
  );
  const adminProductStatus = useSelector(
    (state) => state.adminProductStatus || {},
  );

  const {
    loading: loadingList = false,
    products = [],
    error: listError = "",
    errorMeta: listErrorMeta = null,
  } = adminProductList;

  const {
    loading: loadingDelete = false,
    success: successDelete = false,
    message: deleteMessage = "",
    error: deleteError = "",
    errorMeta: deleteErrorMeta = null,
  } = adminProductDelete;

  const {
    loading: loadingStatus = false,
    success: successStatus = false,
    message: statusMessage = "",
    error: statusError = "",
    errorMeta: statusErrorMeta = null,
  } = adminProductStatus;

  const shopKey = useMemo(
    () => normalizeShopKey(searchParams.get("shopKey")),
    [searchParams],
  );

  const status = useMemo(
    () => normalizeStatus(searchParams.get("status")),
    [searchParams],
  );

  const sort = useMemo(
    () => normalizeSort(searchParams.get("sort")),
    [searchParams],
  );

  const page = useMemo(
    () =>
      normalizePage(
        searchParams.get("page") || searchParams.get("pageNumber") || 1,
      ),
    [searchParams],
  );

  const search = useMemo(
    () => safeText(searchParams.get("search") || searchParams.get("keyword")),
    [searchParams],
  );

  const selectedShopLabel = useMemo(() => getShopLabel(shopKey), [shopKey]);

  const pagination = useMemo(
    () => getPaginationState(adminProductList),
    [adminProductList],
  );

  const effectiveParams = useMemo(
    () =>
      cleanParams({
        shopKey: getApiShopKey(shopKey),
        status,
        search,
        sort,
        page,
        limit: PAGE_LIMIT,
        includeFacets: false,
      }),
    [shopKey, status, search, sort, page],
  );

  const safeProducts = useMemo(
    () => (Array.isArray(products) ? products.filter(Boolean) : []),
    [products],
  );

  const isMutating = loadingDelete || loadingStatus;
  const isBusy = loadingList || isMutating;
  const hasProducts = safeProducts.length > 0;

  const setParam = useCallback(
    (patch = {}) => {
      const next = new URLSearchParams(searchParams);

      Object.entries(patch).forEach(([key, value]) => {
        const text = value === undefined || value === null ? "" : String(value);

        if (!text.trim()) {
          next.delete(key);
        } else {
          next.set(key, text);
        }
      });

      if (
        "shopKey" in patch ||
        "status" in patch ||
        "search" in patch ||
        "sort" in patch
      ) {
        next.set("page", "1");
        next.delete("pageNumber");
      }

      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams],
  );

  const refreshList = useCallback(() => {
    dispatch(listAdminProducts(effectiveParams));
  }, [dispatch, effectiveParams]);

  useEffect(() => {
    refreshList();
  }, [refreshList]);

  useEffect(() => {
    setSearchInput(search);
  }, [search]);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      const nextSearch = searchInput.trim();

      if (nextSearch !== search) {
        setParam({ search: nextSearch });
      }
    }, SEARCH_DEBOUNCE_MS);

    return () => window.clearTimeout(timer);
  }, [searchInput, search, setParam]);

  useEffect(() => {
    setConfirmingId("");
  }, [shopKey, status, search, sort, page]);

  useEffect(() => {
    const message = isConflictError(listErrorMeta)
      ? getConflictMessage(listErrorMeta)
      : normalizeErrorMessage(listError);

    if (!message || lastToastRef.current === message) return;

    lastToastRef.current = message;
    toast.error(message);
  }, [listError, listErrorMeta]);

  useEffect(() => {
    if (!deleteError) return;

    const message = isConflictError(deleteErrorMeta)
      ? getConflictMessage(deleteErrorMeta)
      : normalizeErrorMessage(deleteError);

    if (!message || lastToastRef.current === message) return;

    lastToastRef.current = message;
    toast.error(message);
  }, [deleteError, deleteErrorMeta]);

  useEffect(() => {
    if (!statusError) return;

    const message = isConflictError(statusErrorMeta)
      ? getConflictMessage(statusErrorMeta)
      : normalizeErrorMessage(statusError);

    if (!message || lastToastRef.current === message) return;

    lastToastRef.current = message;
    toast.error(message);
  }, [statusError, statusErrorMeta]);

  useEffect(() => {
    if (!successDelete) return;

    toast.success(deleteMessage || "Product deleted.");
    setConfirmingId("");
    setMutatingProductId("");
    dispatch(resetAdminProductDelete());
    refreshAfterMutationRef.current = true;
  }, [deleteMessage, dispatch, successDelete]);

  useEffect(() => {
    if (!successStatus) return;

    toast.success(statusMessage || "Product status updated.");
    setMutatingProductId("");
    dispatch(resetAdminProductSetStatus());
    refreshAfterMutationRef.current = true;
  }, [dispatch, statusMessage, successStatus]);

  useEffect(() => {
    if (!refreshAfterMutationRef.current) return;

    refreshAfterMutationRef.current = false;
    refreshList();
  }, [refreshList, successDelete, successStatus]);

  useEffect(() => {
    if (!loadingDelete && !loadingStatus) {
      setMutatingProductId("");
    }
  }, [loadingDelete, loadingStatus]);

  useEffect(() => {
    return () => {
      dispatch(resetAdminProductDelete());
      dispatch(resetAdminProductSetStatus());
    };
  }, [dispatch]);

  const handleCreate = useCallback(() => {
    if (shopKey === ALL_SHOPS_KEY) {
      navigate("/admin/products/create");
      return;
    }

    navigate(`/admin/products/create?shopKey=${encodeURIComponent(shopKey)}`);
  }, [navigate, shopKey]);

  const handleDelete = useCallback(
    async (product) => {
      const id = getProductId(product);

      if (!id || isMutating) return;

      setMutatingProductId(id);

      try {
        await dispatch(deleteAdminProduct(id));
      } catch {
        setMutatingProductId("");
      }
    },
    [dispatch, isMutating],
  );

  const handleSetStatus = useCallback(
    async (product, nextStatus) => {
      const id = getProductId(product);
      const currentStatus = getProductStatus(product);
      const normalizedNextStatus = normalizeStatus(nextStatus);

      if (!id || isMutating) return;
      if (normalizedNextStatus === "all") return;
      if (normalizedNextStatus === "deleted") return;
      if (currentStatus === normalizedNextStatus) return;

      setMutatingProductId(id);

      try {
        await dispatch(setAdminProductStatus(id, normalizedNextStatus));
      } catch {
        setMutatingProductId("");
      }
    },
    [dispatch, isMutating],
  );

  const handleRestore = useCallback(
    async (product) => {
      const id = getProductId(product);

      if (!id || isMutating) return;

      setMutatingProductId(id);

      try {
        await dispatch(restoreAdminProduct(id, "draft"));
      } catch {
        setMutatingProductId("");
      }
    },
    [dispatch, isMutating],
  );

  const handlePageChange = useCallback(
    (nextPage) => {
      const safeNextPage = normalizePage(nextPage, page);
      if (safeNextPage === page) return;

      setParam({ page: safeNextPage });
    },
    [page, setParam],
  );

  return (
    <div className="admin-products-page">
      <header className="admin-products-header">
        <div className="admin-products-header__top">
          <h1 className="admin-products-title">Products</h1>

          <button
            type="button"
            className="admin-button admin-button--primary"
            onClick={handleCreate}
            disabled={isMutating}
          >
            + Create product
          </button>
        </div>

        <div className="admin-products-filters">
          <label className="admin-field">
            <span className="admin-field__label">Shop</span>

            <select
              className="admin-select"
              value={shopKey}
              onChange={(event) => setParam({ shopKey: event.target.value })}
              disabled={isBusy}
            >
              {SHOP_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>

          <label className="admin-field">
            <span className="admin-field__label">Status</span>

            <select
              className="admin-select"
              value={status}
              onChange={(event) => setParam({ status: event.target.value })}
              disabled={isBusy}
            >
              {STATUS_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>

          <label className="admin-field admin-field--grow">
            <span className="admin-field__label">Search</span>

            <input
              className="admin-input"
              value={searchInput}
              placeholder="Search name, SKU, slug, brand..."
              onChange={(event) => setSearchInput(event.target.value)}
              disabled={isBusy}
            />
          </label>

          <label className="admin-field">
            <span className="admin-field__label">Sort</span>

            <select
              className="admin-select"
              value={sort}
              onChange={(event) => setParam({ sort: event.target.value })}
              disabled={isBusy}
            >
              {SORT_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>
        </div>

        <div className="admin-products-meta">
          <span>
            {pagination.total} product{pagination.total === 1 ? "" : "s"} ·{" "}
            {selectedShopLabel} · {status}
          </span>

          {(loadingList || isMutating) && (
            <span className="admin-products-meta__loading">Loading…</span>
          )}
        </div>
      </header>

      <main className="admin-products-body">
        {loadingList && !hasProducts ? (
          <div className="admin-feedback admin-feedback--info">
            Loading products...
          </div>
        ) : null}

        {!loadingList && !hasProducts ? (
          <div className="admin-card admin-card--ghost">
            <p className="admin-card__title">No products found</p>
            <p className="admin-card__subtitle">
              Try changing shop, status, or search terms.
            </p>
          </div>
        ) : null}

        {hasProducts ? (
          <div className="admin-products-table">
            <div className="admin-products-table__head">
              <div>Name</div>
              <div>SKU</div>
              <div>Price</div>
              <div>Stock</div>
              <div>Status</div>
              <div className="admin-products-table__actions">Actions</div>
            </div>

            {safeProducts.map((product) => {
              const id = getProductId(product);
              const productStatus = getProductStatus(product);

              const isDeleted = productStatus === "deleted";
              const isPublished = productStatus === "published";
              const isDraft = productStatus === "draft";
              const isRowMutating = mutatingProductId === id;

              return (
                <div
                  key={id}
                  className="admin-products-table__row"
                  aria-busy={isRowMutating}
                >
                  <div className="admin-products-table__cell admin-products-table__name">
                    <div className="admin-products-table__nameLine">
                      <strong>{product?.name || "Untitled product"}</strong>

                      {product?.isVariant ? (
                        <span className="admin-pill">Variant</span>
                      ) : null}

                      {product?.productType ? (
                        <span className="admin-pill admin-pill--muted">
                          {String(product.productType)}
                        </span>
                      ) : null}
                    </div>

                    <div className="admin-products-table__subline">
                      {product?.brand || product?.slug || "—"}
                    </div>

                    <div className="admin-muted">
                      Updated {formatDate(product?.updatedAt)}
                    </div>
                  </div>

                  <div className="admin-products-table__cell">
                    <code className="admin-code">{product?.sku || "—"}</code>
                  </div>

                  <div className="admin-products-table__cell">
                    £{formatMoney(product?.priceFrom ?? product?.price)}
                  </div>

                  <div className="admin-products-table__cell">
                    {getAvailableLabel(product)}
                  </div>

                  <div className="admin-products-table__cell">
                    <span className={getStatusBadgeClass(productStatus)}>
                      {productStatus}
                    </span>
                  </div>

                  <div className="admin-products-table__actions">
                    <div className="admin-actions-split">
                      <Link
                        className="admin-button admin-button--small admin-button--ghost"
                        to={`/admin/products/${id}/edit`}
                      >
                        Edit
                      </Link>

                      {isDeleted ? (
                        <button
                          type="button"
                          className="admin-button admin-button--small admin-button--ghost"
                          onClick={() => handleRestore(product)}
                          disabled={isMutating}
                        >
                          Restore draft
                        </button>
                      ) : null}

                      {!isDeleted && isDraft ? (
                        <button
                          type="button"
                          className="admin-button admin-button--small admin-button--primary"
                          onClick={() => handleSetStatus(product, "published")}
                          disabled={isMutating}
                        >
                          Publish
                        </button>
                      ) : null}

                      {!isDeleted && isPublished ? (
                        <button
                          type="button"
                          className="admin-button admin-button--small admin-button--ghost"
                          onClick={() => handleSetStatus(product, "draft")}
                          disabled={isMutating}
                        >
                          Draft
                        </button>
                      ) : null}

                      {!isDeleted && confirmingId !== id ? (
                        <button
                          type="button"
                          className="admin-button admin-button--small admin-button--danger"
                          onClick={() => setConfirmingId(id)}
                          disabled={isMutating}
                        >
                          Delete
                        </button>
                      ) : null}

                      {!isDeleted && confirmingId === id ? (
                        <span className="admin-confirm">
                          <button
                            type="button"
                            className="admin-button admin-button--small admin-button--danger"
                            onClick={() => handleDelete(product)}
                            disabled={isMutating}
                          >
                            Confirm
                          </button>

                          <button
                            type="button"
                            className="admin-button admin-button--small admin-button--ghost"
                            onClick={() => setConfirmingId("")}
                            disabled={isMutating}
                          >
                            Cancel
                          </button>
                        </span>
                      ) : null}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : null}

        {pagination.totalPages > 1 ? (
          <nav className="admin-pagination" aria-label="Product pagination">
            <button
              type="button"
              className="admin-button admin-button--small admin-button--ghost"
              onClick={() => handlePageChange(page - 1)}
              disabled={!pagination.hasPrevPage || isBusy}
            >
              Previous
            </button>

            <span className="admin-pagination__meta">
              Page {pagination.page} of {pagination.totalPages}
            </span>

            <button
              type="button"
              className="admin-button admin-button--small admin-button--ghost"
              onClick={() => handlePageChange(page + 1)}
              disabled={!pagination.hasNextPage || isBusy}
            >
              Next
            </button>
          </nav>
        ) : null}
      </main>
    </div>
  );
};

export default AdminProductsListScreen;
